import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS, LOGO_URL } from "../data/content";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Treat home page AND investors page as dark-nav variant (over navy hero).
  // Without this, on /investors the translucent light nav bar sits over the
  // navy hero — illegible labels and an indistinct active underline.
  const isDarkHero =
    location.pathname === "/" || location.pathname === "/investors";

  // On the investors page, relabel the CTA so a prospective investor doesn't
  // land on the general contact page. Anchors to the gate form instead.
  const isInvestors = location.pathname === "/investors";
  const ctaLabel = isInvestors ? "Enquire" : "Talk to us";
  const ctaTo = isInvestors ? "/investors#gate" : "/contact";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navClass = scrolled
    ? "di-nav"
    : isDarkHero
    ? "di-nav-dark"
    : "di-nav";

  const linkColor = scrolled
    ? "var(--color-on-surface)"
    : isDarkHero
    ? "rgba(249,249,248,0.78)"
    : "var(--color-on-surface)";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${navClass}`}
      style={{ height: 72 }}
    >
      <div className="di-container h-full flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <img
            src={LOGO_URL}
            alt="DesignIndya"
            style={{
              height: 28,
              filter: scrolled
                ? "none"
                : isDarkHero
                ? "brightness(0) invert(1)"
                : "none",
              transition: "filter 0.2s ease",
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            // Hash-aware active match — also lights up "Insights" (/#insights)
            // when the user has navigated to that section on the home page.
            const current = location.pathname + (location.hash || "");
            const active = l.to.includes("#")
              ? l.to === current
              : l.to === location.pathname;
            return (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative label-sm transition-opacity"
                style={{
                  color: active ? "var(--color-tertiary)" : linkColor,
                  paddingBottom: 4,
                  fontWeight: active ? 600 : 500,
                  borderBottom: active ? "1.5px solid var(--color-tertiary)" : "1.5px solid transparent",
                }}
                onMouseEnter={(e) => !active && (e.currentTarget.style.opacity = "0.55")}
                onMouseLeave={(e) => !active && (e.currentTarget.style.opacity = "1")}
              >
                {l.label}
              </Link>
            );
          })}
          <Link
            to={ctaTo}
            data-testid="nav-cta-talk"
            className="btn"
            style={{
              backgroundColor: scrolled ? "var(--color-primary)" : isDarkHero ? "var(--color-tertiary)" : "var(--color-primary)",
              color: "var(--color-on-primary)",
            }}
          >
            {ctaLabel}
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 -mr-2"
          style={{ color: linkColor }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden"
          style={{
            background: "var(--color-surface)",
            borderTop: "1px solid var(--color-outline)",
            padding: "var(--space-32) var(--space-24)",
          }}
        >
          <nav className="flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="display-sm flex items-center gap-3"
                style={{ color: "var(--color-primary)", fontWeight: 700 }}
              >
                <span className="dot-sm" />
                {l.label}
              </Link>
            ))}
            <Link
              to={ctaTo}
              data-testid="mobile-nav-cta-talk"
              className="btn btn-primary mt-4 self-start"
            >
              {ctaLabel}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
